import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Stack, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getWaitingRoom, updateCheckin, updateAppointment, removeCheckin } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { useNavigate } from 'react-router-dom';

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function minutesWaiting(checkedInAt) {
  if (!checkedInAt) return 0;
  const d = new Date(checkedInAt + (checkedInAt.endsWith('Z') ? '' : 'Z'));
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

// Puntualidad del paciente (checked_in_at vs start_time) — distinto de
// "esperando" (checked_in_at vs ahora, que mide al consultorio/médico, no
// al paciente). Pedido explícito del usuario 2026-11-02: son dos cosas
// separadas y no deben mezclarse en un solo número. null si llegó a
// horario o antes (no hay nada que mostrar en ese caso).
function minutesLate(startTime, checkedInAt) {
  if (!startTime || !checkedInAt) return null;
  const start = new Date(startTime + (startTime.endsWith('Z') ? '' : 'Z'));
  const checkin = new Date(checkedInAt + (checkedInAt.endsWith('Z') ? '' : 'Z'));
  const diff = Math.floor((checkin.getTime() - start.getTime()) / 60000);
  return diff > 0 ? diff : null;
}

export default function WaitingRoom({ onCallPatient }) {
  const { isRecepcionista, isAdmin, isMedico } = useAuth();
  const notify = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialog, setNoteDialog] = useState(null); // { appt, note }
  const [saving, setSaving] = useState(false);

  // Mismas acciones para los tres roles — pedido explícito del usuario
  // (2026-11-02): en consultorios chicos cualquiera hace de recepcionista
  // de cualquier médico, así que la sala de espera no debe distinguir
  // "qué puede hacer cada rol", salvo "Llamar paciente" (abre la ficha
  // para cargar la consulta, solo tiene sentido si hay un onCallPatient
  // real pasado desde el padre). El backend ya no filtra la cola por
  // médico (acceso total a toda la clínica desde 2026-10-09).
  const canManage = isRecepcionista || isAdmin || isMedico;

  const fetch = useCallback(async () => {
    try {
      const r = await getWaitingRoom();
      setQueue(r.data);
    } catch {
      // silently fail on poll
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 20000);
    return () => clearInterval(interval);
  }, [fetch]);

  const move = async (appt, direction) => {
    const idx = queue.findIndex((a) => a.id === appt.id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= queue.length) return;

    const newQueue = [...queue];
    [newQueue[idx], newQueue[newIdx]] = [newQueue[newIdx], newQueue[idx]];
    newQueue.forEach((a, i) => { a.waiting_order = i; });
    setQueue([...newQueue]);

    try {
      await updateCheckin(appt.id, { waiting_order: newIdx });
      await updateCheckin(newQueue[idx].id, { waiting_order: idx });
    } catch {
      notify('Error al reordenar', 'error');
      fetch();
    }
  };

  const callPatient = async (appt) => {
    try {
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      onCallPatient?.(appt);
      notify(`${appt.patient?.name} llamado a consulta`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al llamar paciente', 'error');
    }
  };

  const markAusente = async (appt) => {
    try {
      await updateAppointment(appt.id, { state: 'ausente' });
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      notify(`${appt.patient?.name} marcado como ausente`, 'warning');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  const removeFromQueue = async (appt) => {
    try {
      await removeCheckin(appt.id);
      setQueue((q) => q.filter((a) => a.id !== appt.id));
      notify('Paciente removido de la sala de espera', 'info');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await updateCheckin(noteDialog.appt.id, { reception_note: noteDialog.note });
      setQueue((q) =>
        q.map((a) => a.id === noteDialog.appt.id ? { ...a, reception_note: noteDialog.note } : a)
      );
      setNoteDialog(null);
    } catch (err) {
      notify(err.userMessage || 'Error al guardar nota', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6">Sala de espera</Typography>
        <Chip
          label={queue.length === 0 ? 'Vacía' : `${queue.length} paciente${queue.length > 1 ? 's' : ''}`}
          size="small"
          color={queue.length > 0 ? 'primary' : 'default'}
        />
      </Stack>

      {queue.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No hay pacientes esperando
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {queue.map((appt, idx) => {
            const mins = minutesWaiting(appt.checked_in_at);
            const waitColor = mins > 30 ? 'error.main' : mins > 15 ? 'warning.main' : 'text.secondary';
            // Puntualidad del paciente (vs. espera en sala, arriba) — ver
            // minutesLate más arriba.
            const late = minutesLate(appt.start_time, appt.checked_in_at);

            return (
              <Paper
                key={appt.id}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  borderLeft: '4px solid',
                  borderLeftColor: idx === 0 ? 'primary.main' : 'divider',
                }}
              >
                {/* Posición */}
                <Typography
                  sx={{
                    minWidth: 24, fontWeight: 700, fontSize: 14,
                    color: idx === 0 ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {idx + 1}
                </Typography>

                {/* Info paciente */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                    onClick={() => navigate(`/patients/${appt.patient_id}`)}
                    noWrap
                  >
                    {appt.patient?.name || 'Paciente'}
                  </Typography>
                  {/* Dos líneas fijas en vez de un único Stack con
                      flexWrap — con médico/hora/tipo/esperando/llegó
                      tarde/nota todos en la misma fila flexible, en mobile
                      cada uno arrancaba en una posición distinta según
                      cuánto entrara antes, dando un efecto "escalonado"
                      (reportado con captura 2026-11-02). Línea 1: datos
                      fijos del turno (no cambian con el tiempo). Línea 2:
                      los dos indicadores de tiempo, que son los que más
                      varían en ancho según los minutos. */}
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      {appt.doctor?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Turno {formatTime(appt.start_time)}
                    </Typography>
                    {/* Tipo de turno — pedido explícito del usuario
                        2026-11-02, mismo dato que ya se ve en la agenda del
                        día y el listado de Turnos. Precargado por
                        joinedload en el backend (GET /appointments/waiting-room). */}
                    {appt.appointment_type && (
                      <>
                        <Typography variant="caption" color="text.secondary">·</Typography>
                        <Typography variant="caption" sx={{ color: appt.appointment_type.color, fontWeight: 700 }}>
                          {appt.appointment_type.name}
                        </Typography>
                      </>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 12, color: waitColor }} />
                      <Typography variant="caption" color={waitColor}>
                        {mins}min esperando
                      </Typography>
                    </Stack>
                    {/* Llegó tarde (checked_in_at vs. start_time) — distinto
                        de "esperando" arriba, que mide al consultorio/médico
                        (cuánto lleva en sala), no al paciente (si llegó a
                        horario). Pedido explícito del usuario 2026-11-02:
                        "una cosa es la espera, y otra diferente la llegada
                        tarde". Chip propio para no mezclar los dos números. */}
                    {late !== null && (
                      <Chip
                        label={`Llegó ${late}min tarde`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'error.50', color: 'error.dark' }}
                      />
                    )}
                    {appt.reception_note && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: 'italic',
                          color: 'warning.dark',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        "{appt.reception_note}"
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* Acciones — en mobile pasan a su propia fila a ancho
                    completo (con flexWrap en el Paper contenedor): hasta 5
                    ícono-botones compitiendo con el nombre del paciente en
                    una sola fila quedaban demasiado apretados. */}
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={isMobile ? { flexBasis: '100%', justifyContent: 'flex-end', pl: 4 } : undefined}
                >
                  {canManage && (
                    <>
                      <Tooltip title="Reordenar: subir">
                        <span>
                          <IconButton size="small" disabled={idx === 0} onClick={() => move(appt, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reordenar: bajar">
                        <span>
                          <IconButton size="small" disabled={idx === queue.length - 1} onClick={() => move(appt, 1)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar nota de recepción">
                        <IconButton
                          size="small"
                          onClick={() => setNoteDialog({ appt, note: appt.reception_note || '' })}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {onCallPatient && (
                    <Tooltip title="Llamar paciente e iniciar consulta">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => callPatient(appt)}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canManage && (
                    <Tooltip title="Marcar ausente">
                      <IconButton size="small" color="warning" onClick={() => markAusente(appt)}>
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canManage && (
                    <Tooltip title="Quitar de sala de espera">
                      <IconButton size="small" onClick={() => removeFromQueue(appt)}>
                        <PersonOffIcon fontSize="small" sx={{ color: 'error.main' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Edit note dialog */}
      <Dialog open={!!noteDialog} onClose={() => setNoteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nota de recepción</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="Ej: está borracho, tiene acompañante, trae estudios..."
            value={noteDialog?.note || ''}
            onChange={(e) => setNoteDialog((d) => ({ ...d, note: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveNote} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
